if (!customElements.get("m-collection-list")) {
  class MCollectionList extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
        slideInner: ".m-mixed-layout__inner",
        gridPagination: "[data-grid-pagination]",
        gridPaginationList: "[data-grid-pagination] .m-pagination",
      };
      this.classes = {
        grid: 'm:grid',
        hidden: 'm:hidden',
        swiper: 'swiper-container',
        swiperWrapper: 'swiper-wrapper'
      };
      this.domNodes = queryDomNodes(this.selectors, this);

      this.slider = false;

      this.enableSlider = this.dataset.enableSlider === "true";
      this.items = this.dataset.items;
      this.total = this.dataset.total;
      this.autoplay = this.dataset.autoplay === "true";
      this.autoplaySpeed = this.dataset.autoplaySpeed;
      this.paginationType = this.dataset.paginationType;
      this.expanded = this.dataset.expanded === "true";
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      this.enableGridPagination = this.dataset.enableGridPagination === "true";
      this.gridItemsPerPage = parseInt(this.dataset.gridItemsPerPage) || 0;
      this.gridPage = 1;
      this.gridPaginationInitialized = false;

      this.init();
      document.addEventListener("matchMobile", () => {
        this.init();
      });
      document.addEventListener("unmatchMobile", () => {
        this.init();
      });
    }

    init() {
      this.initGridPagination();
      if (!this.enableSlider) return;
      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        this.destroySlider();
      } else {
        this.initSlider();
      }
    }

    initGridPagination() {
      if (!this.enableGridPagination || this.enableSlider || this.gridItemsPerPage < 1) return;

      const { gridPagination, gridPaginationList } = queryDomNodes(this.selectors, this);
      const slideInner = this.querySelector(this.selectors.slideInner);
      if (!slideInner) return;

      this.gridItems = Array.from(slideInner.children);
      this.gridTotalPages = Math.ceil(this.gridItems.length / this.gridItemsPerPage);

      if (!gridPagination || !gridPaginationList || this.gridTotalPages <= 1) return;
      if (this.gridPaginationInitialized) {
        this.setGridPage(this.gridPage);
        return;
      }

      gridPagination.addEventListener("click", (event) => {
        const pageLink = event.target.closest("[data-grid-pagination-page]");
        if (!pageLink) return;

        event.preventDefault();
        this.setGridPage(parseInt(pageLink.dataset.gridPaginationPage, 10), true);
      });
      this.gridPaginationInitialized = true;
      this.setGridPage(1);
    }

    setGridPage(page, scrollToGrid = false) {
      this.gridPage = Math.min(Math.max(page, 1), this.gridTotalPages);

      this.gridItems.forEach((item, index) => {
        const itemPage = Math.floor(index / this.gridItemsPerPage) + 1;
        item.classList.toggle(this.classes.hidden, itemPage !== this.gridPage);
      });

      this.renderGridPagination();

      if (scrollToGrid) {
        const content = this.querySelector(".m-collection-list__content");
        content && content.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }

    renderGridPagination() {
      const { gridPaginationList } = queryDomNodes(this.selectors, this);
      if (!gridPaginationList) return;

      const pages = this.getGridPaginationPages();
      const items = [];

      if (this.gridPage > 1) {
        items.push(this.renderGridPaginationItem("&laquo;", this.gridPage - 1, "prev"));
      }

      pages.forEach((page) => {
        if (page === "...") {
          items.push('<span class="deco">...</span>');
        } else if (page === this.gridPage) {
          items.push(`<span class="page current">${page}</span>`);
        } else {
          items.push(this.renderGridPaginationItem(page, page, "page"));
        }
      });

      if (this.gridPage < this.gridTotalPages) {
        items.push(this.renderGridPaginationItem("&raquo;", this.gridPage + 1, "next"));
      }

      gridPaginationList.innerHTML = items.join("");
    }

    renderGridPaginationItem(label, page, className) {
      return `<span class="${className}"><a href="#" data-grid-pagination-page="${page}">${label}</a></span>`;
    }

    getGridPaginationPages() {
      if (this.gridTotalPages <= 5) {
        return Array.from({ length: this.gridTotalPages }, (_, index) => index + 1);
      }

      if (this.gridPage <= 3) {
        return [1, 2, 3, "...", this.gridTotalPages];
      }

      if (this.gridPage >= this.gridTotalPages - 2) {
        return [1, "...", this.gridTotalPages - 2, this.gridTotalPages - 1, this.gridTotalPages];
      }

      return [1, "...", this.gridPage, "...", this.gridTotalPages];
    }

    initSlider() {
      const { slideContainer, slideControls, slideInner } = queryDomNodes(this.selectors, this);

      const prevButton = slideControls && slideControls.querySelector(".m-slider-controls__button-prev");
      const nextButton = slideControls && slideControls.querySelector(".m-slider-controls__button-next");
      const paginationIcon = `<svg width="65px" height="65px" viewBox="0 0 72 72" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><circle class="time" stroke-width="5" fill="none" stroke-linecap="round" cx="33" cy="33" r="28"></circle></svg>`;

      this.options = {
        slidesPerView: 2,
        loop: false,
        slidesPerGroup: 1,
        showNavigation: true,
        showPagination: true,
        parallax: true,
        autoplay: this.autoplay
          ? {
            delay: parseInt(this.autoplaySpeed) * 1000,
          }
          : false,
        pagination:
          this.paginationType == "fraction"
            ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              type: "fraction",
            }
            : {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
              bulletClass: "m-dot",
              bulletActiveClass: "m-dot--active",
              renderBullet: function (index, className) {
                return '<span class="' + className + '">' + paginationIcon + "</span>";
              },
            },
        navigation: nextButton || prevButton ? {
          nextEl: nextButton && nextButton,
          prevEl: prevButton && prevButton
        } : false,
        autoHeight: true,
        on: {
          init: (swiper) => {
            this.updateNavigationState(swiper, prevButton, nextButton);
          },
          slideChange: (swiper) => {
            this.updateNavigationState(swiper, prevButton, nextButton);
          },
          breakpoint: (swiper) => {
            this.updateNavigationState(swiper, prevButton, nextButton);
          },
        },
        breakpoints: {
          480: {
            slidesPerView: 3,
          },
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          1280: {
            slidesPerView: this.expanded && this.total > this.items ? parseInt(this.items) + 1 : parseInt(this.items),
          },
        }
      };

      if (typeof this.slider !== 'object') {
        slideContainer.classList.add(this.classes.swiper);
        slideInner.classList.remove(this.classes.grid);
        slideInner.classList.add(this.classes.swiperWrapper);
        slideControls && slideControls.classList.remove(this.classes.hidden);
        this.slider = new MinimogLibs.Swiper(slideContainer, this.options);
        this.updateNavigationState(this.slider, prevButton, nextButton);
      }
    }

    updateNavigationState(swiper, prevButton, nextButton) {
      if (!swiper) return;
      this.toggleNavigationButton(prevButton, swiper.isBeginning);
      this.toggleNavigationButton(nextButton, swiper.isEnd);
    }

    toggleNavigationButton(button, disabled) {
      if (!button) return;
      button.disabled = disabled;
      button.setAttribute("aria-disabled", disabled ? "true" : "false");
      button.classList.toggle("swiper-button-disabled", disabled);
      button.style.setProperty("color", disabled ? "#9b9b9b" : "");
      button.style.setProperty("opacity", disabled ? "0.35" : "");
      button.style.setProperty("cursor", disabled ? "not-allowed" : "");
      button.style.setProperty("pointer-events", disabled ? "none" : "");
    }

    destroySlider() {
      const { slideContainer, slideControls, slideInner } = queryDomNodes(this.selectors, this);

      slideContainer.classList.remove(this.classes.swiper);
      slideInner.classList.remove(this.classes.swiperWrapper);
      slideInner.classList.add(this.classes.grid);
      slideControls && slideControls.classList.add(this.classes.hidden);

      if (typeof this.slider === 'object') {
        this.slider.destroy();
        this.slider = false;
      }
    }
  }

  customElements.define("m-collection-list", MCollectionList);
}
